import Tooltip from 'components/base/Tooltip'
import { HostType } from 'configs/theme'
import { c, FC } from 'lib/component-utils'
import { useTheme } from 'lib/colors'
import { useUserContext } from 'lib/context'
import { useIntl } from 'lib/intl-utils'

type Accreditation = {
  alt: string
  id: string
  darkLogoSrc?: string
  href?: string
  logoClassName: string
  logoSrc: string
}

const accreditationLogoPath = '/next/img/footer/accreditation-logos'

const sellmonitorAccreditations: Accreditation[] = [
  {
    alt: 'Sellmonitor - официальный авторизованный сервис Wildberries',
    id: 'wb',
    href: 'https://seller.wildberries.ru/auth-services',
    darkLogoSrc: `${accreditationLogoPath}/wb-dark.svg`,
    logoSrc: `${accreditationLogoPath}/wb.svg`,
    logoClassName: 'right-[6.5px] bottom-[8.5px] w-[38px]',
  },
  {
    alt: 'Sellmonitor - сервис-партнёр Ozon',
    id: 'ozon',
    darkLogoSrc: `${accreditationLogoPath}/ozon-dark.svg`,
    logoSrc: `${accreditationLogoPath}/ozon.svg`,
    logoClassName: 'right-[6.5px] bottom-2.5 w-[64px]',
  },
  {
    alt: 'Sellmonitor - официальный партнёр Яндекс Маркета',
    id: 'yandex-market',
    href: 'https://partner.market.yandex.ru/welcome/partners-page',
    darkLogoSrc: `${accreditationLogoPath}/yandex-market-dark.svg`,
    logoSrc: `${accreditationLogoPath}/yandex-market.svg`,
    logoClassName: 'right-[6.5px] bottom-[8.5px] w-[42px]',
  },
  {
    alt: 'Sellmonitor - отечественное программное обеспечение в реестре Минцифры',
    id: 'mincifri',
    href: 'https://reestr.digital.gov.ru/reestr/2100122/',
    darkLogoSrc: `${accreditationLogoPath}/mincifri-dark.svg`,
    logoSrc: `${accreditationLogoPath}/mincifri.svg`,
    logoClassName: 'right-[8.5px] bottom-[7.5px] size-[29px]',
  },
  {
    alt: 'Sellmonitor - резидент ИНТЦ «Сириус»',
    id: 'sirius',
    href: 'https://intc.sirius.ru/residents/',
    darkLogoSrc: `${accreditationLogoPath}/sirius-dark.svg`,
    logoSrc: `${accreditationLogoPath}/sirius.svg`,
    logoClassName: 'right-[7px] bottom-[5px] size-[33px]',
  },
]

const mayakAccreditations: Accreditation[] = [
  {
    alt: 'Маяк — официальный авторизованный сервис Wildberries',
    id: 'wb',
    href: 'https://seller.wildberries.ru/auth-services',
    darkLogoSrc: `${accreditationLogoPath}/wb-dark.svg`,
    logoSrc: `${accreditationLogoPath}/wb.svg`,
    logoClassName: 'right-[6.5px] bottom-[8.5px] w-[38px]',
  },
  {
    alt: 'Маяк — резидент Иннополиса',
    id: 'innopolis',
    darkLogoSrc: `${accreditationLogoPath}/innopolis-dark.svg`,
    logoSrc: `${accreditationLogoPath}/innopolis.svg`,
    logoClassName: 'left-[6px] bottom-[10px] w-[68px]',
  },
  {
    alt: 'Маяк включён в реестр малых технологических компаний',
    id: 'mtk',
    href: 'https://disk.yandex.ru/i/RudLx7O4jR962Q',
    darkLogoSrc: `${accreditationLogoPath}/mtk-dark.png`,
    logoSrc: `${accreditationLogoPath}/mtk.png`,
    logoClassName: 'right-[8px] bottom-[8px] size-[28px]',
  },
]

const accreditationsByHost: Partial<Record<HostType, Accreditation[]>> = {
  sellmonitor: sellmonitorAccreditations,
  mayak: mayakAccreditations,
}

const AccreditationCard: FC<{ accreditation: Accreditation; title: string }> = ({
  accreditation,
  title,
}) => {
  const { isDark } = useTheme()
  const logoSrc =
    isDark && accreditation.darkLogoSrc !== undefined
      ? accreditation.darkLogoSrc
      : accreditation.logoSrc

  const card = (
    <div className="relative flex size-[80px] rounded-md bg-gray-100 dark:bg-gray-200 pt-[3px] pl-[4px] text-[7px] leading-[1.3] font-medium [font-variation-settings:'opsz'_12] text-black">
      <div className="whitespace-pre-line">{title}</div>
      <img src={logoSrc} alt={accreditation.alt} className={c`absolute ${accreditation.logoClassName}`} />
    </div>
  )

  if (accreditation.href === undefined) {
    return card
  }

  return (
    <a href={accreditation.href} target="_blank" rel="noreferrer nofollow">
      {card}
    </a>
  )
}

const FooterAccreditations: FC = () => {
  const { theme } = useUserContext()
  const { t } = useIntl()
  const accreditations = accreditationsByHost[theme.hostType]

  if (accreditations === undefined) {
    return null
  }

  return (
    <div className="grid grid-cols-3 gap-3 w-fit sm:grid-cols-6">
      {accreditations.map((accreditation) => {
        const key = `footer.accreditations.${theme.hostType}.${accreditation.id}`

        return (
          <Tooltip
            key={accreditation.id}
            sizing="text"
            content={
              <div>
                <div className="font-medium">{t(`${key}.heading`)}</div>
                <div className={c`mt-0.5 ${accreditation.id === 'sirius'} max-w-[360px]`}>
                  {t(`${key}.description`)}
                </div>
                {accreditation.id === 'sirius' && (
                  <div className="mt-2.5 mb-0.5">
                    <img
                      loading="lazy"
                      src="/next/img/footer/sirius_view.webp"
                      alt={t(`${key}.image_alt`)}
                      className="max-w-[360px] rounded-md"
                    />
                  </div>
                )}
              </div>
            }
          >
            <span>
              <AccreditationCard accreditation={accreditation} title={t(`${key}.title`)} />
            </span>
          </Tooltip>
        )
      })}
    </div>
  )
}

export default FooterAccreditations
